import { ReactNode, useId } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { useTranslation } from 'react-i18next';

export interface FieldControlProps {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': true | undefined;
}

export interface FieldProps {
    label: string;
    hint?: string;
    /** Replaces the hint and marks the control invalid. */
    error?: string;
    /** Marks the field "(optional)"; required fields get no asterisk. */
    optional?: boolean;
    children: (control: FieldControlProps) => ReactNode;
}

/** Label above the control, hint or error below, wired with aria-describedby. */
export const Field = ({ label, hint, error, optional, children }: FieldProps) => {
    const { t } = useTranslation();
    const id = useId();
    const describedBy = error || hint ? `${id}-desc` : undefined;
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={id} className="text-small font-medium text-strong">
                {label}
                {optional && <span className="font-normal text-muted"> {t('ui.optional')}</span>}
            </label>
            {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
            {error ? (
                <p id={describedBy} className="flex items-start gap-1.5 text-caption text-danger">
                    <ExclamationCircleIcon className="mt-px size-4 shrink-0" aria-hidden="true" />
                    {error}
                </p>
            ) : (
                hint && (
                    <p id={describedBy} className="text-caption text-muted">
                        {hint}
                    </p>
                )
            )}
        </div>
    );
};
